// Crawl-status watcher for the Catalog pages (TDC-1499): polls GET
// /rmn/crawls (active + just-finished window) while anything is in flight,
// renders in-progress crawls as placeholder rows, and fires `onFinished`
// exactly once when a crawl this page watched settles — the page toasts and
// refreshes its list. A completed 'brand' URL-add crawl doesn't count as
// finished until its import step wrote stats.url_import (the crawl row
// completes minutes before the brand/product promotes do).

import { useCallback, useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import axiosInstant from '../../api'
import { rmnCrawlStopEndpoint, rmnCrawlsEndpoint } from '../../api/endpoints'

export interface RmnCrawlRow {
  id: number
  status: 'queued' | 'running' | 'completed' | 'failed' | string
  // 'brand' = add-by-URL (an import step follows the crawl);
  // 'brand-crawl' = per-row re-crawl of a brand's shelf (crawl only).
  kind: 'category' | 'brand' | 'brand-crawl' | null
  // Set on per-row re-crawls (the target is known up front); URL-adds only
  // learn names mid-crawl.
  category_name: string | null
  brand_name: string | null
  // Per-row brand re-analyses only ('brand-crawl'): the network brand row
  // whose shelf is being re-analyzed.
  brand_id?: number | null
  root_url: string
  // false + kind null = an admin full-site analysis (covers every target).
  scoped?: boolean
  scope_category_id: number | null
  pages_fetched: number
  products_discovered: number
  // Graceful stop: stop_requested = the Stop button was pressed and the
  // worker is wrapping up ("Stopping" pill); stopped = the crawl actually
  // ended early on request (it still completes — everything found is kept).
  stop_requested?: boolean
  stopped?: boolean
  url_import: {
    brand_id?: number
    brand_name?: string
    products_imported?: number
    products_failed?: number
    report_id?: number
    category_name?: string
    error?: string
  } | null
  error: string | null
  created_at: string
  finished_at: string | null
}

export const isCrawlInProgress = (c: RmnCrawlRow): boolean => {
  if (c.status === 'queued' || c.status === 'running') return true
  // Brand URL-adds keep importing after the crawl completes.
  return c.kind === 'brand' && c.status === 'completed' && !c.url_import
}

// Brand-side analyses: URL adds ('brand') and per-row shelf re-analyses
// ('brand-crawl'). Everything else — category adds/re-analyses and admin
// site crawls — grows the category tree, so it belongs on Categories.
export const isBrandKindCrawl = (c: RmnCrawlRow): boolean =>
  c.kind === 'brand' || c.kind === 'brand-crawl'

// Admin full-site analysis: unscoped and untagged. The API refuses every
// new analysis while one runs (it already covers every target), so the
// Catalog pages pause their Analyze/Add buttons for it — and only for it.
export const isSiteCrawl = (c: RmnCrawlRow): boolean => !c.kind && !c.scoped

const POLL_MS = 8000

export function useRmnCrawls({
  onFinished,
}: {
  onFinished?: (crawl: RmnCrawlRow) => void
} = {}) {
  const [crawls, setCrawls] = useState<RmnCrawlRow[]>([])
  const watchedRef = useRef<Set<number>>(new Set())
  const onFinishedRef = useRef(onFinished)
  onFinishedRef.current = onFinished

  const refresh = useCallback(async () => {
    try {
      const res = await axiosInstant.get(rmnCrawlsEndpoint)
      const rows = (res.data || []) as RmnCrawlRow[]
      rows.forEach((row) => {
        if (isCrawlInProgress(row)) {
          watchedRef.current.add(row.id)
        } else if (watchedRef.current.has(row.id)) {
          // Settled while we were watching — announce once.
          watchedRef.current.delete(row.id)
          onFinishedRef.current?.(row)
        }
      })
      setCrawls(rows)
    } catch {
      // Keep the last rows; the next poll retries.
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const hasActive = crawls.some(isCrawlInProgress)
  useEffect(() => {
    if (!hasActive) return undefined
    const id = setInterval(refresh, POLL_MS)
    return () => clearInterval(id)
  }, [hasActive, refresh])

  return { crawls, refresh }
}

// Placeholder-row description, shared by the Categories and Brands pages.
export const crawlRowLabel = (crawl: RmnCrawlRow): string => {
  if (crawl.category_name) return `Analyzing ${crawl.category_name}`
  // Brand ADDS carry the name the client typed, so they must be checked
  // before the generic brand_name branch (which is the per-row re-analysis
  // wording — that brand is already in the catalog).
  if (crawl.kind === 'brand') {
    return crawl.brand_name
      ? `Adding ${crawl.brand_name}`
      : `Adding brand — analyzing ${crawlHostname(crawl.root_url)}`
  }
  if (crawl.brand_name) return `Analyzing ${crawl.brand_name}'s products`
  if (crawl.kind === 'category') return `Adding category — analyzing ${crawlHostname(crawl.root_url)}`
  return `Analyzing ${crawlHostname(crawl.root_url)}`
}

// Placeholder-row pill text, shared by the Categories and Brands pages.
export const crawlStatusLabel = (crawl: RmnCrawlRow): string => {
  if (crawl.stop_requested && isCrawlInProgress(crawl)) return 'Stopping'
  if (crawl.status === 'queued') return 'Queued'
  // A completed brand URL-add is still importing its products.
  if (crawl.status === 'completed') return 'Importing'
  return 'Analyzing'
}

// True while the Stop button makes sense: the crawl loop is still going and
// no stop is pending. (A completed-but-importing brand crawl can't stop —
// the crawl part is already over.)
export const isCrawlStoppable = (crawl: RmnCrawlRow): boolean =>
  (crawl.status === 'queued' || crawl.status === 'running') && !crawl.stop_requested

// Ask the API to gracefully stop a crawl. Toasts the outcome; returns
// whether the request landed so callers can refresh their crawl list.
export const requestCrawlStop = async (crawl: RmnCrawlRow): Promise<boolean> => {
  try {
    await axiosInstant.post(rmnCrawlStopEndpoint(crawl.id))
    toast.success(
      'Stopping the analysis — everything it already found will still be processed.',
    )
    return true
  } catch (e: any) {
    if (e?.response?.status === 409) {
      toast.error('That analysis has already finished.')
      return true // settled either way — worth a refresh
    }
    toast.error('Couldn\'t stop the analysis. Please try again.')
    return false
  }
}

export const crawlHostname = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

// User-facing failure reason for a settled crawl.
export const crawlFailureMessage = (crawl: RmnCrawlRow): string => {
  if (crawl.error === 'RootUrlNotAllowed' || crawl.error === 'NoAllowedDomains') {
    return 'The analysis couldn\'t start — that URL can\'t be analyzed for your network.'
  }
  return 'The analysis failed. Please try again, or contact your account manager if it keeps failing.'
}
